import { FieldErrors } from 'react-hook-form';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { CampaignTypeEnum } from '@shared/constants/campaign.constants';
import { VITE_SKIP_INBOUND_SIP_CONFIG } from '@shared/constants/environments';
import { SidebarSubTabFieldsMap, SidebarTabNames } from '@pages/Admin/Campaign/ui/constants';
import { SidebarTabNamesType } from '@pages/Admin/Campaign/ui/types';
import { formatList } from '@shared/utils/formatList';
import { CreateCampaignFormValue } from '../model/type';

export type CampaignValidationMessage = {
  key: string;
  params?: Record<string, string>;
  settingKeys?: string[];
};

export type CampaignStartBlocker = {
  tab?: SidebarTabNamesType;
  message: CampaignValidationMessage;
  isFormField?: boolean;
};

type TranslateFn = (key: string, params?: Record<string, string>) => string;

const FIELD_MESSAGE_KEYS: Record<string, string> = {
  agents: 'adminMainPage.errors.atLeastOneAgent',
  title: 'adminMainPage.campaigns.validation.titleRequiredForStart',
  callSchedule: 'adminMainPage.campaigns.validation.scheduleRequiredForStart',
  retryIntervalInMinutes: 'adminMainPage.campaigns.validation.retryFieldRequiredForStart',
  retryMaxNumber: 'adminMainPage.campaigns.validation.retryFieldRequiredForStart',
  retriesPerDay: 'adminMainPage.campaigns.validation.retryFieldRequiredForStart',
  reservedLimit: 'adminMainPage.campaigns.validation.reservedLimitRequiredForStart',
  'analysisPlan.summaryPrompt': 'adminMainPage.campaigns.validation.analysisPromptRequiredForStart',
  followUpTemplate: 'adminMainPage.campaigns.validation.followUpTemplateRequiredForStart',
  leadList: 'adminMainPage.campaigns.validation.noPhoneNumbers',
};

const FIELD_LABEL_KEYS: Record<string, string> = {
  retryIntervalInMinutes: 'campaignPage.retryDelay',
  retryMaxNumber: 'campaignPage.overallRetryAttempt',
  retriesPerDay: 'campaignPage.retryPerDay',
};

export const flattenFormErrorFields = (errors: FieldErrors, prefix = ''): string[] => {
  const fields: string[] = [];

  for (const key of Object.keys(errors)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const error = errors[key as keyof typeof errors];

    if (!error) {
      continue;
    }

    if (typeof error === 'object' && 'type' in error && error.type) {
      fields.push(path);
    } else if (typeof error === 'object') {
      fields.push(...flattenFormErrorFields(error as FieldErrors, path));
    }
  }

  return fields;
};

export const getTabForField = (field: string): SidebarTabNamesType | undefined => {
  const tab = SidebarSubTabFieldsMap[field] ?? SidebarSubTabFieldsMap[field.split('.')[0]];
  return tab as SidebarTabNamesType | undefined;
};

export const getFieldBlockerMessage = (field: string): CampaignValidationMessage => {
  const key = FIELD_MESSAGE_KEYS[field] ?? 'adminMainPage.errors.fillTheField';
  const fieldLabelKey = FIELD_LABEL_KEYS[field];

  return {
    key,
    ...(fieldLabelKey ? { params: { fieldName: fieldLabelKey } } : {}),
  };
};

export interface CampaignDialTargetsParams {
  campaignType?: string;
  hasCustomers: boolean;
  hasSipNumbers: boolean;
}

export const hasCampaignDialTargets = ({
  campaignType,
  hasCustomers,
  hasSipNumbers,
}: CampaignDialTargetsParams): boolean => {
  const isInbound = campaignType === CampaignTypeEnum.INBOUND;

  return hasCustomers || (isInbound && (hasSipNumbers || VITE_SKIP_INBOUND_SIP_CONFIG));
};

export type AgentRemovedSettingsBlockerInput = {
  agentName: string;
  settingKeys: string[];
};

export type AgentSettingsCheckStatus = 'pending' | 'failed' | 'ready';

export interface CampaignStartBlockersParams {
  isRunningLoading: boolean;
  isLoading: boolean;
  isLoadingCampaignAgents: boolean;
  isStoppingLoading: boolean;
  campaignStatus?: string;
  campaignType?: string;
  hasAgents: boolean;
  hasConfiguredPhoneNumbers: boolean;
  currentUserRole?: string;
  // Omitted without a form (campaign list)
  formErrors?: FieldErrors<CreateCampaignFormValue>;
  // Omitted without agent data (campaign list)
  agentsWithRemovedSettings?: AgentRemovedSettingsBlockerInput[];
  agentSettingsCheck?: AgentSettingsCheckStatus;
}

export const getCampaignStartBlockers = ({
  isRunningLoading,
  isLoading,
  isLoadingCampaignAgents,
  isStoppingLoading,
  campaignStatus,
  campaignType,
  hasAgents,
  hasConfiguredPhoneNumbers,
  currentUserRole,
  formErrors,
  agentsWithRemovedSettings,
  agentSettingsCheck = 'ready',
}: CampaignStartBlockersParams): CampaignStartBlocker[] => {
  if (isRunningLoading) {
    return [{ message: { key: 'adminMainPage.campaigns.validation.campaignStarting' } }];
  }

  if (isLoading || isLoadingCampaignAgents) {
    return [{ message: { key: 'adminMainPage.campaigns.validation.loadingCampaignData' } }];
  }

  if (isStoppingLoading) {
    return [{ message: { key: 'adminMainPage.campaigns.validation.campaignStopping' } }];
  }

  const blockers: CampaignStartBlocker[] = [];

  if (!hasAgents) {
    blockers.push({
      tab: SidebarTabNames.AGENT,
      message: { key: 'adminMainPage.errors.atLeastOneAgent' },
    });
  }

  // Without the company lists a removed setting cannot be detected, so do not allow a start
  if (hasAgents && agentSettingsCheck === 'pending') {
    blockers.push({
      message: { key: 'adminMainPage.campaigns.validation.checkingAgentSettings' },
    });
  }

  if (hasAgents && agentSettingsCheck === 'failed') {
    blockers.push({
      tab: SidebarTabNames.AGENT,
      message: { key: 'adminMainPage.campaigns.validation.agentSettingsCheckFailed' },
    });
  }

  for (const { agentName, settingKeys } of agentsWithRemovedSettings ?? []) {
    if (!settingKeys.length) continue;

    blockers.push({
      tab: SidebarTabNames.AGENT,
      message: {
        key: 'adminMainPage.campaigns.validation.agentRemovedSettings',
        params: { agentName },
        settingKeys,
      },
    });
  }

  if (!hasConfiguredPhoneNumbers) {
    const isInbound = campaignType === CampaignTypeEnum.INBOUND;

    blockers.push(
      isInbound
        ? {
            tab: SidebarTabNames.SIP_TRUNK,
            message: { key: 'adminMainPage.campaigns.validation.hasSipTrunks' },
          }
        : {
            tab: SidebarTabNames.PHONE_NUMBERS,
            message: { key: 'adminMainPage.campaigns.validation.noPhoneNumbers' },
          },
    );
  }

  const errorFields = formErrors ? flattenFormErrorFields(formErrors) : [];

  for (const field of errorFields) {
    if (field === 'agents') {
      continue;
    }

    blockers.push({
      tab: getTabForField(field),
      message: getFieldBlockerMessage(field),
      isFormField: true,
    });
  }

  if (currentUserRole === UserRolesEnum.VIEWER) {
    blockers.push({ message: { key: 'adminMainPage.campaigns.validation.noPermissionToStart' } });
  }

  return blockers;
};

export const translateBlockerMessage = (blocker: CampaignStartBlocker, t: TranslateFn): string => {
  const { settingKeys } = blocker.message;
  const translatedParams = blocker.message.params
    ? Object.fromEntries(
        Object.entries(blocker.message.params).map(([key, value]) => [
          key,
          key === 'fieldName' ? t(value) : value,
        ]),
      )
    : undefined;
  const params = settingKeys?.length
    ? {
        ...translatedParams,
        settings: formatList(
          settingKeys.map((key) => t(key)),
          t,
        ),
      }
    : translatedParams;

  return t(blocker.message.key, params);
};

export const getPrimaryDisabledReason = (
  blockers: CampaignStartBlocker[],
  t: TranslateFn,
): string => {
  if (!blockers.length) {
    return '';
  }

  const first = blockers[0];
  const isOperationalBlocker =
    !first.isFormField &&
    (!first.tab ||
      first.tab === SidebarTabNames.AGENT ||
      first.tab === SidebarTabNames.PHONE_NUMBERS);

  if (isOperationalBlocker) {
    return translateBlockerMessage(first, t);
  }

  const formFieldBlockers = blockers.filter((blocker) => blocker.isFormField);

  if (formFieldBlockers.length > 1) {
    const issues = formFieldBlockers
      .map((blocker) => translateBlockerMessage(blocker, t))
      .join('; ');
    return t('adminMainPage.campaigns.validation.cannotStartFixIssues', { issues });
  }

  return translateBlockerMessage(first, t);
};

export const getTabIssueMessages = (
  blockers: CampaignStartBlocker[],
  t: TranslateFn,
): Partial<Record<SidebarTabNamesType, string>> => {
  const byTab: Partial<Record<SidebarTabNamesType, string[]>> = {};

  for (const blocker of blockers) {
    if (!blocker.tab) {
      continue;
    }

    const message = translateBlockerMessage(blocker, t);

    if (!byTab[blocker.tab]) {
      byTab[blocker.tab] = [];
    }

    if (!byTab[blocker.tab]!.includes(message)) {
      byTab[blocker.tab]!.push(message);
    }
  }

  return Object.fromEntries(
    Object.entries(byTab).map(([tab, messages]) => [tab, (messages ?? []).join('; ')]),
  ) as Partial<Record<SidebarTabNamesType, string>>;
};

export const getIssuedTabsFromBlockers = (
  blockers: CampaignStartBlocker[],
): SidebarTabNamesType[] => {
  const tabs = blockers
    .map((blocker) => blocker.tab)
    .filter((tab): tab is SidebarTabNamesType => Boolean(tab));
  return [...new Set(tabs)];
};

export const isCampaignFinishing = (status?: string | null): boolean => status === 'finishing';

export const isCampaignDialing = (status?: string | null): boolean =>
  status === 'active' || status === 'finishing';

export type CampaignListRunInput = {
  status?: string | null;
  type?: string;
  agentsCount?: number;
  customersTotal?: number;
  hasSipTrunks?: boolean;
  currentUserRole?: string;
  isRunningLoading?: boolean;
  isStoppingLoading?: boolean;
};

/**
 * Start/stop rules for a campaign list row. Inactive rows use the same blockers
 * as the editor. An active row can still be stopped, except by a viewer.
 * Finishing stays visible and cannot be started again.
 */
export const getCampaignListRunControl = (
  input: CampaignListRunInput,
  t: TranslateFn,
): { disabled: boolean; reason: string } => {
  if (input.isStoppingLoading) {
    return {
      disabled: true,
      reason: t('adminMainPage.campaigns.validation.campaignStopping'),
    };
  }

  if (input.isRunningLoading) {
    return {
      disabled: true,
      reason: t('adminMainPage.campaigns.validation.campaignStarting'),
    };
  }

  if (isCampaignFinishing(input.status)) {
    return { disabled: true, reason: '' };
  }

  if (input.status === 'active') {
    const isViewer = input.currentUserRole === UserRolesEnum.VIEWER;
    return {
      disabled: isViewer,
      reason: isViewer ? t('adminMainPage.campaigns.validation.noPermissionToStart') : '',
    };
  }

  const blockers = getCampaignStartBlockers({
    isRunningLoading: false,
    isLoading: false,
    isLoadingCampaignAgents: false,
    isStoppingLoading: false,
    campaignStatus: input.status ?? undefined,
    campaignType: input.type,
    hasAgents: (input.agentsCount ?? 0) > 0,
    hasConfiguredPhoneNumbers: hasCampaignDialTargets({
      campaignType: input.type,
      hasCustomers: (input.customersTotal ?? 0) > 0,
      hasSipNumbers: Boolean(input.hasSipTrunks),
    }),
    currentUserRole: input.currentUserRole,
  });

  return {
    disabled: blockers.length > 0,
    reason: getPrimaryDisabledReason(blockers, t),
  };
};
